import { useAppContext } from "../../context/AppContext";
import { ProfileTrans, SuccessTrans, ScheduleTrans, DaysOfTheWeekTrans, translateMonth as translateMonthUtil, formatDateLocalized, getPracticeScoreLabel } from "../../utils/translations";
import bgImg from "../../assets/images/bgImg-3.png";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { getLinks } from "../../services/linksService";
import { trackPixelEvent } from "../../utils/pixel";
import { cancelSubscription, payUnpaidPass, claimGift } from "../../services/passService";
import { cancelBooking } from "../../services/classService";
import { getUser } from "../../services/userService";
import ClassModal from "../Schedule/ClassModal";
import BookingsByPass from "../shared/BookingsByPass";
import RegularDaysCard from "../Schedule/RegularDaysCard";
import TelegramSection from "../Schedule/TelegramSection";
import WhatsAppSection from "../Schedule/WhatsAppSection";
import { hasActiveStudioPass, hasActiveOnlineClubPass, hasMultiClassPass } from "../../utils/communityPassHelpers";
import PWAInstallBanner from "../utilPages/PWAInstallBanner";

export default function Profile() {
  const { language, user, token, setUser, setToaster, getNextBillingDate, setLoading } = useAppContext();
  const [showConfirm, setShowConfirm] = useState(false);
  const [links, setLinks] = useState([]);
  const modalRef = useRef(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [showClassModal, setShowClassModal] = useState(false);
  const [cancelingFromModal, setCancelingFromModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const showGiftClaim = searchParams.get("claim") === "gift";
  const codeFromUrl = searchParams.get("code") || "";
  const [giftClaimCode, setGiftClaimCode] = useState("");
  const [giftClaiming, setGiftClaiming] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);

  const referralShareUrl = useMemo(() => {
    if (!user?.user_id || typeof window === "undefined") return "";
    return `${window.location.origin}/schedule?ref=${encodeURIComponent(user.user_id)}`;
  }, [user?.user_id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Prefill gift code from URL when opening profile with ?claim=gift&code=XXX
  useEffect(() => {
    if (showGiftClaim && codeFromUrl) {
      setGiftClaimCode(codeFromUrl.trim().toUpperCase());
    }
  }, [showGiftClaim, codeFromUrl]);

  const scheduleDetails = ScheduleTrans[language];
  const daysTrans = DaysOfTheWeekTrans[language];

  // Accepts either a pass type object (preferred, reads name from the DB) or a raw name string
  const translatePassName = (passTypeOrName) => {
    const pt = passTypeOrName && typeof passTypeOrName === "object" ? passTypeOrName : null;
    const name = pt ? pt.name : passTypeOrName;
    if (pt?.onlineOnly) return scheduleDetails.onlineClub;
    if (pt) {
      const dbName = language === "russian" ? (pt.displayNameRu || pt.displayName) : pt.displayName;
      if (dbName) return dbName;
    }
    const passMap = {
      trial: scheduleDetails.trial,
      park: scheduleDetails.park,
      single: scheduleDetails.dropInClass,
      basic: scheduleDetails.basicPass,
      standard: scheduleDetails.standartPass,
      limitless: scheduleDetails.limitless,
      onlineClub: scheduleDetails.onlineClub,
      "individual-1": scheduleDetails["individual-1"],
      "individual-4": scheduleDetails["individual-4"],
      "individual-8": scheduleDetails["individual-8"],
      "individual-12": scheduleDetails["individual-12"],
    };
    return passMap[name] ?? scheduleDetails[name] ?? name;
  };

  const translateMonth = (monthAbbr) => translateMonthUtil(monthAbbr, daysTrans);

  const getBookingForClass = (classId) => {
    if (!user?.bookings) return undefined;
    const id = (b) => (b.classId && typeof b.classId === "object" ? b.classId._id : b.classId);
    return user.bookings.find(
      (b) => (id(b) && String(id(b)) === String(classId)) && b.status === "confirmed"
    );
  };

  const handleCancelBooking = async (classId) => {
    try {
      await cancelBooking(classId, token);
      const updatedUser = await getUser(token);
      setUser(updatedUser);
      setShowClassModal(false);
      setSelectedClass(null);
      setCancelingFromModal(false);
      setToaster({ message: scheduleDetails.bookingCancelled || "Booking cancelled", type: "success", show: true });
    } catch (err) {
      console.error("Error cancelling booking:", err);
      setToaster({ message: scheduleDetails.bookingCancelledError || "Failed to cancel booking", type: "error", show: true });
    }
  };

  const translateLocationName = (locationName) => {
    const locationMap = {
      "Park Leumi": scheduleDetails.parkLeumi,
      "Studio": scheduleDetails.studio,
      "Ramat Gan Rooftop": scheduleDetails.ramatGanRooftop,
    };
    return locationMap[locationName] || locationName;
  };

  const isPassExhausted = (pass) => {
    return pass.classesIncluded && pass.classesUsed >= pass.classesIncluded;
  };

  // Only show subscription UI (cancel button / "Subscription cancelled" note) for passes that are actually subscriptions
  const isSubscriptionPass = (pass) =>
    pass.isSubscription === true &&
    (pass.passTypeId?.subscription?.enabled !== false);

  const now = new Date();
  // Statistics use only confirmed bookings; cancelled are excluded
  const isConfirmedBooking = (b) => b.status === "confirmed";
  const hasValidClass = (b) => b.classId && (typeof b.classId === "object" ? b.classId.start : true);
  const getClassStart = (b) => (typeof b.classId === "object" && b.classId?.start) ? new Date(b.classId.start) : null;
  const countsTowardAttendance = (b) => b.attended !== false;
  const confirmedPastBookingsCount =
    user?.bookings?.filter(
      (b) =>
        isConfirmedBooking(b) &&
        hasValidClass(b) &&
        countsTowardAttendance(b) &&
        new Date(typeof b.classId === "object" ? b.classId.start : 0) < now
    )?.length || 0;
  const practicesCount =
    (user?.completedPractices?.length || 0) + confirmedPastBookingsCount;

  // Current passes: not expired and (active, exhausted, or paused), paid only
  const activePasses = user?.passes?.filter(
    (p) =>
      p.paid !== false &&
      new Date(p.expiresAt) > now &&
      (p.status === "active" || p.status === "exhausted" || p.status === "paused")
  );
  // Unpaid passes: not expired, show with Pay button
  const unpaidPasses = user?.passes?.filter(
    (p) =>
      p.paid === false &&
      new Date(p.expiresAt) > now &&
      (p.status === "active" || p.status === "exhausted" || p.status === "paused")
  );
  // Past passes: only when actually expired by date or status
  const expiredPasses = user?.passes?.filter(
    (p) => new Date(p.expiresAt) <= now || p.status === "expired" || p.status === "cancelled"
  );

  const upcomingBookings = (user?.bookings || [])
    .filter((b) => isConfirmedBooking(b) && hasValidClass(b) && new Date(typeof b.classId === "object" ? b.classId.start : 0) > now)
    .sort((a, b) => (getClassStart(a)?.getTime() ?? 0) - (getClassStart(b)?.getTime() ?? 0))
    .slice(0, 3)
    .map((b) => ({ ...b, key: b.classId?._id || b._id, sortDate: getClassStart(b)?.getTime() ?? 0 }));

  const pastBookings = (user?.bookings || [])
    .filter((b) => isConfirmedBooking(b) && hasValidClass(b) && new Date(typeof b.classId === "object" ? b.classId.start : 0) < now)
    .sort((a, b) => (getClassStart(b)?.getTime() ?? 0) - (getClassStart(a)?.getTime() ?? 0))
    .slice(0, 50)
    .map((b) => ({ ...b, key: b.classId?._id || b._id, sortDate: getClassStart(b)?.getTime() ?? 0 }));

  const earliestBookingStart = (user?.bookings || [])
    .filter((b) => isConfirmedBooking(b) && hasValidClass(b) && (typeof b.classId === "object" ? b.classId.start : true))
    .reduce((earliest, b) => {
      const start = typeof b.classId === "object" ? b.classId.start : null;
      if (!start) return earliest;
      const d = new Date(start);
      return !earliest || d < earliest ? d : earliest;
    }, null);
  const createdAt = user?.created_at ? new Date(user.created_at) : null;
  const memberSinceDate =
    createdAt && earliestBookingStart
      ? (createdAt < earliestBookingStart ? createdAt : earliestBookingStart)
      : createdAt || earliestBookingStart;

  const getPracticeLevel = (count) => {
    if (count >= 50) return { key: "levelLegend", emoji: "🌟" };
    if (count >= 25) return { key: "levelDevoted", emoji: "🌿" };
    if (count >= 10) return { key: "levelRegular", emoji: "✨" };
    return { key: "levelBeginner", emoji: "🌱" };
  };
  const practiceLevel = getPracticeLevel(practicesCount);
  const getNextLevelInfo = (count) => {
    if (count >= 50) return null;
    if (count >= 25) return { nextThreshold: 50 };
    if (count >= 10) return { nextThreshold: 25 };
    return { nextThreshold: 10 };
  };
  const nextLevelInfo = getNextLevelInfo(practicesCount);
  const practicesLeftUntilNext = nextLevelInfo ? Math.max(0, nextLevelInfo.nextThreshold - practicesCount) : 0;

  const activeSubscriptionPass = (user?.passes || []).find(
    (p) =>
      isSubscriptionPass(p) &&
      p.subscriptionActive &&
      new Date(p.expiresAt) > now
  );

  useEffect(() => {
    if (!links.length) {
      (async () => {
        const fetchedLinks = await getLinks();
        setLinks(fetchedLinks);
      })();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowConfirm(false);
      }
    };
    if (showConfirm) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showConfirm]);

  const handleCancelSubscription = async () => {
    setShowConfirm(false);
    const passId = activeSubscriptionPass?._id;
    if (!passId) return;
    try {
      setLoading(true);
      await cancelSubscription(passId, token);
      const updatedUser = await getUser(token);
      setUser(updatedUser);
      setLoading(false);
      setToaster({
        message: language === "russian" ? "Подписка отменена" : "Subscription cancelled",
        type: "success",
        show: true,
      });
    } catch (error) {
      console.error("Error cancelling subscription", error);
      setLoading(false);
      setToaster({
        message: language === "russian"
          ? "Ошибка при отмене подписки. Пожалуйста, попробуйте снова или напишите на anna.joy.inbox@gmail.com."
          : "Failed to cancel subscription. Please try again or contact anna.joy.inbox@gmail.com.",
        type: "error",
        show: true,
      });
    }
  };

  const getIntensityNumber = (registerDate, practicesCompleted) => {
    if (!user?.created_at) return 0;
    const today = new Date();
    const startDate = new Date(registerDate);
    const totalWeeks = Math.ceil(
      (today - startDate) / (1000 * 60 * 60 * 24 * 7)
    );
    return totalWeeks > 0 ? Math.round(Number((practicesCompleted / totalWeeks).toFixed(1))) : 0;
  };

  const calculateIntensity = (registerDate, practicesCompleted) => {
    const intensity = getIntensityNumber(registerDate, practicesCompleted);
    if (intensity === 0) return null;
    return `${intensity} ${language === "russian" ? "р/нед" : "x/week"}`;
  };

  return (
    <main className="min-h-screen flex flex-col bg-gradient-to-b from-amber-50/80 to-gray-50 pb-24">
      {/* Hero with overlay */}
      <div
        className="relative z-0 w-full h-[38vh] min-h-[200px] sm:h-[42vh] sm:min-h-[240px] bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.4)), url(${bgImg})`,
          backgroundPosition: "center 30%",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-end pb-4 px-4 sm:pb-6 sm:px-6">
          <div
            className={`flex items-end gap-3 sm:gap-4 ${
              !user?.picture ? "justify-center text-center sm:justify-start sm:text-left" : ""
            }`}
          >
            {user?.picture && (
              <img
                src={user.picture}
                alt=""
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-4 border-white shadow-xl ring-2 ring-amber-100/50 flex-shrink-0"
              />
            )}
            <div className="pb-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-white drop-shadow-md truncate">
                {user?.name || user?.nickname || user?.username || ProfileTrans[language].profile}
              </h1>
              <p className="text-amber-100 text-xs sm:text-sm mt-0.5">
                {ProfileTrans[language].memberSince}{" "}
                {memberSinceDate &&
                  memberSinceDate.toLocaleDateString(language === "russian" ? "ru-RU" : "en-GB", {
                    month: "short",
                    year: "numeric",
                  })}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 -mt-2 relative z-10">
        {/* Score / Level card */}
        <div className="rounded-2xl shadow-xl bg-white border border-amber-100/80 overflow-hidden mb-6 sm:mb-8">
          <div className="bg-white px-10 py-4 sm:py-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-700/80 mb-1">
              {ProfileTrans[language].yourScore}
            </p>
            <div className="flex items-center justify-between gap-2 sm:gap-3 flex-nowrap">
              <div className="flex items-baseline gap-1.5 sm:gap-3 min-w-0">
                <span className="text-2xl sm:text-4xl font-bold text-amber-800 tabular-nums shrink-0">{practicesCount}</span>
                <span className="text-sm sm:text-lg text-amber-700 truncate">{getPracticeScoreLabel(practicesCount, language)}</span>
              </div>
              <div className="shrink-0">
                <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs sm:text-sm font-medium whitespace-nowrap">
                  {practiceLevel.emoji} {ProfileTrans[language][practiceLevel.key]}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {nextLevelInfo ? (
                <>{practicesLeftUntilNext} {getPracticeScoreLabel(practicesLeftUntilNext, language)} {ProfileTrans[language].toNextLevel}</>
              ) : (
                ProfileTrans[language].youAreLegend
              )}
            </p>
            {getIntensityNumber(user?.created_at, practicesCount) > 0 && (
              <p className="text-sm text-amber-600 mt-2">
                {calculateIntensity(user?.created_at, practicesCount)}
              </p>
            )}
          </div>
        </div>

        {/* Gift card redeem — 8march-style section above passes */}
        {showGiftClaim && (
          <div className="mb-6 sm:mb-8 rounded-2xl overflow-hidden flex flex-col items-center bg-white shadow-[0_20px_40px_rgba(44,41,37,0.08)]">
            <div className="w-full max-w-md p-6 sm:p-8 text-center">
              <span className="inline-block text-[#8a9b76] text-lg mb-2" aria-hidden="true">✦</span>
              <h3
                className="text-xl sm:text-2xl font-semibold text-[#2c2925] text-center mb-3"
                style={{ fontFamily: '"Cormorant Garamond", serif' }}
              >
                {ProfileTrans[language].activateGiftCode}
              </h3>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
                <input
                  type="text"
                  value={giftClaimCode}
                  onChange={(e) => setGiftClaimCode(e.target.value.toUpperCase())}
                  placeholder={ProfileTrans[language].activateGiftPlaceholder}
                  className="flex-1 min-w-0 px-4 py-3 rounded-full border-2 border-[#d49a82] bg-white text-[#2c2925] text-center text-base font-medium placeholder:text-[#a8b896] focus:outline-none focus:border-[#c67b5c] focus:ring-2 focus:ring-[#c67b5c]/20 transition-colors"
                  maxLength={12}
                />
                <button
                  type="button"
                  disabled={!token || !giftClaimCode.trim() || giftClaiming}
                  onClick={async () => {
                    if (!token || !giftClaimCode.trim()) return;
                    setGiftClaiming(true);
                    try {
                      await claimGift(giftClaimCode.trim(), token);
                      const updatedUser = await getUser(token);
                      setUser(updatedUser);
                      setToaster({
                        message: language === "russian" ? "Подарок активирован!" : "Gift activated!",
                        type: "success",
                        show: true,
                      });
                      setGiftClaimCode("");
                      setSearchParams({}, { replace: true });
                    } catch (e) {
                      setToaster({
                        message: e?.message || (language === "russian" ? "Не удалось активировать код" : "Failed to activate code"),
                        type: "error",
                        show: true,
                      });
                    } finally {
                      setGiftClaiming(false);
                    }
                  }}
                  className="px-6 py-3 rounded-full bg-[#c67b5c] text-[#faf8f5] text-base font-semibold shadow-[0_4px_16px_rgba(198,123,92,0.35)] hover:bg-[#d49a82] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-[#c67b5c]"
                >
                  {giftClaiming ? "…" : ProfileTrans[language].activateGiftButton}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* My passes (active) */}
        <div className="mb-6 sm:mb-8 bg-white p-4 sm:p-6 rounded-2xl shadow-lg border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{ProfileTrans[language].myPasses}</h3>
          {/* Unpaid passes - yellow with Pay button, same layout and progress bar as active */}
          {unpaidPasses?.length > 0 && (
            <div className="flex flex-wrap gap-4 justify-center mb-4">
              {unpaidPasses.map((pass) => {
                const isPausedUnpaid = pass.status === "paused";
                return (
                <div
                  key={pass._id}
                  className={`border-2 rounded-xl px-6 shadow-lg p-4 w-full min-w-[200px] max-w-[280px] ${
                    isPausedUnpaid ? "bg-blue-50 border-blue-400" : "bg-amber-50 border-amber-400"
                  }`}
                >
                  <div className="flex flex-col items-center text-center">
                    <h3 className={`text-base font-semibold mb-2 ${isPausedUnpaid ? "text-blue-800" : "text-amber-800"}`}>
                      {translatePassName(pass.passTypeId || pass.name)}
                    </h3>
                    <p className={`text-sm mb-1 ${isPausedUnpaid ? "text-blue-700" : "text-amber-700"}`}>
                      {ProfileTrans[language].unpaidPass} — ₪{pass.finalPrice}
                    </p>
                    <div className={`text-sm w-full ${isPausedUnpaid ? "text-blue-700" : "text-amber-700"}`}>
                      {pass.classesIncluded != null ? (
                        <>
                          <p>
                            {pass.classesUsed ?? 0} / {pass.classesIncluded} {scheduleDetails.classesUsed}
                          </p>
                          <div className={`w-full rounded-full h-2 mt-2 overflow-hidden ${isPausedUnpaid ? "bg-blue-200" : "bg-amber-200"}`}>
                            <div
                              className={`h-full transition-all ${isPausedUnpaid ? "bg-blue-500" : "bg-amber-500"}`}
                              style={{
                                width: `${Math.min(100, ((pass.classesUsed ?? 0) / pass.classesIncluded) * 100)}%`,
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <p className="font-semibold">♾️ {scheduleDetails.unlimited}</p>
                      )}
                      <p className={`mt-2 text-xs ${isPausedUnpaid ? "text-blue-600" : "text-amber-600"}`}>
                        {isPausedUnpaid
                          ? scheduleDetails.paused
                          : `${scheduleDetails.expiresOn}: ${format(new Date(pass.expiresAt), "dd")} ${translateMonth(format(new Date(pass.expiresAt), "MMM"))} ${format(new Date(pass.expiresAt), "yyyy")}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await payUnpaidPass(pass._id, token);
                          if (res?.paymentUrl) window.location.href = res.paymentUrl;
                        } catch (e) {
                          setToaster({ message: e?.message || scheduleDetails.bookingCancelledError, type: "error", show: true });
                        }
                      }}
                      className="mt-3 px-4 py-2 rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600 text-sm"
                    >
                      {ProfileTrans[language].payForPass}
                    </button>
                  </div>
                </div>
              );})}
            </div>
          )}
          {activePasses?.length > 0 ? (
            <div className="flex flex-wrap gap-4 justify-center">
              {activePasses.map((pass) => {
                const isPaused = pass.status === "paused";
                return (
                <div
                  key={pass._id}
                  className={`border-2 rounded-xl px-6 shadow-lg p-4 w-full min-w-[200px] max-w-[280px] ${
                    isPaused
                      ? "bg-blue-50 border-blue-500"
                      : isPassExhausted(pass)
                        ? "bg-orange-50 border-orange-400"
                        : "bg-green-50 border-green-500"
                  }`}
                >
                  <div className="flex flex-col items-center text-center">
                    <h3
                      className={`text-base font-semibold mb-2 ${
                        isPaused ? "text-blue-800" : isPassExhausted(pass) ? "text-orange-800" : "text-green-800"
                      }`}
                    >
                      {translatePassName(pass.passTypeId || pass.name)}
                    </h3>
                    <div
                      className={`text-sm w-full ${
                        isPaused ? "text-blue-700" : isPassExhausted(pass) ? "text-orange-700" : "text-green-700"
                      }`}
                    >
                      {pass.classesIncluded ? (
                        <>
                          <p>
                            {pass.classesUsed} / {pass.classesIncluded} {scheduleDetails.classesUsed}
                          </p>
                          <div
                            className={`w-full rounded-full h-2 mt-2 overflow-hidden ${
                              isPaused ? "bg-blue-200" : isPassExhausted(pass) ? "bg-orange-200" : "bg-green-200"
                            }`}
                          >
                            <div
                              className={`h-full ${isPaused ? "bg-blue-500" : isPassExhausted(pass) ? "bg-orange-400" : "bg-green-600"}`}
                              style={{
                                width: `${(pass.classesUsed / pass.classesIncluded) * 100}%`,
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <p className="font-semibold">♾️ {scheduleDetails.unlimited}</p>
                      )}
                      <p className="mt-2 text-xs">
                        {isPaused
                          ? scheduleDetails.paused
                          : `${pass.isSubscription && pass.subscriptionActive ? scheduleDetails.nextBillingDate : scheduleDetails.expiresOn}: ${format(new Date(pass.expiresAt), "dd")} ${translateMonth(format(new Date(pass.expiresAt), "MMM"))} ${format(new Date(pass.expiresAt), "yyyy")}`}
                      </p>
                    </div>
                  </div>
                  {isSubscriptionPass(pass) && (
                    <div className="mt-1 w-full text-center">
                      {pass.subscriptionActive ? (
                          <button
                            type="button"
                            onClick={() => setShowConfirm(true)}
                            className="mt-2 text-sm text-orange-600 hover:underline"
                          >
                            {ProfileTrans[language].changePlan}
                          </button>
                      ) : (
                          <p className="mt-1 text-xs text-gray-500">
                            {ProfileTrans[language].subscriptionCancelledNote}
                          </p>
                      )}
                    </div>
                  )}
                  {pass.allpayOrderId && String(pass.allpayOrderId).startsWith("gift-") && (
                    <p className="mt-2 text-center text-sm text-gray-600">
                      <Link to="/schedule" className="text-brown underline hover:opacity-90">
                        {ProfileTrans[language].goToScheduleNote}
                      </Link>
                    </p>
                  )}
                </div>
              );})}
            </div>
          ) : !unpaidPasses?.length ? (
            <p className="text-gray-600 text-sm">{scheduleDetails.noActivePass}</p>
          ) : null}
        </div>

        {hasMultiClassPass(user) && (
          <RegularDaysCard user={user} token={token} language={language} setUser={setUser} setToaster={setToaster} />
        )}

        {/* Past passes */}
        {expiredPasses?.length > 0 && (
          <div className="mb-6 sm:mb-8 bg-white p-4 sm:p-6 rounded-2xl shadow-lg border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{ProfileTrans[language].pastPasses}</h3>
            <div className="flex flex-wrap gap-4 max-h-80 overflow-y-auto pr-1 -mr-1">
              {expiredPasses.map((pass) => (
                <div
                  key={pass._id}
                  className={`border-2 mx-auto rounded-xl px-6 shadow p-4 min-w-[140px] ${
                    pass.status === "exhausted" ? "bg-orange-50 border-orange-400" : "bg-gray-100 border-gray-400"
                  }`}
                >
                  <div className="flex flex-col items-center text-center">
                    <h4 className="text-base font-semibold text-gray-600 mb-2">
                      {translatePassName(pass.passTypeId || pass.name)}
                    </h4>
                    <div className="text-sm text-gray-600 w-full">
                      {pass.classesIncluded ? (
                        <>
                          <p>{pass.classesUsed} / {pass.classesIncluded} {scheduleDetails.classesUsed}</p>
                          <div
                            className={`w-full rounded-full h-2 mt-2 overflow-hidden ${
                              pass.status === "exhausted" ? "bg-orange-200" : "bg-gray-300"
                            }`}
                          >
                            <div
                              className={`h-full ${pass.status === "exhausted" ? "bg-orange-400" : "bg-gray-500"}`}
                              style={{
                                width: `${(pass.classesUsed / pass.classesIncluded) * 100}%`,
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <p className="font-semibold">♾️ {scheduleDetails.unlimited}</p>
                      )}
                      <p className="mt-2 text-xs">
                        {new Date(pass.expiresAt) > now ? scheduleDetails.expiresOn : scheduleDetails.expiredAt}: {format(new Date(pass.expiresAt), "dd")} {translateMonth(format(new Date(pass.expiresAt), "MMM"))} {format(new Date(pass.expiresAt), "yyyy")}
                      </p>
                      {pass.status === "exhausted" && (
                        <p className="mt-2 text-xs text-green-600 font-medium">
                          {ProfileTrans[language].passExhausted}
                        </p>
                      )}
                      {pass.isSubscription && pass.expiresAt && (
                        <p className="mt-2 text-xs">
                          {pass.status === "cancelled" && new Date(pass.expiresAt) > now ? (
                            <span className="text-orange-600">
                              {ProfileTrans[language].subscriptionCancelled} · {ProfileTrans[language].accessUntil}: {formatDateLocalized(pass.expiresAt, daysTrans)}
                            </span>
                          ) : (
                            <span className="text-gray-500">
                              {ProfileTrans[language].subscriptionPast} · {ProfileTrans[language].endedOn}: {formatDateLocalized(pass.expiresAt, daysTrans)}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past bookings */}
        <div className="mb-6 sm:mb-8 bg-white p-4 sm:p-6 rounded-2xl shadow-lg border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">📋 {ProfileTrans[language].pastBookings}</h3>
          <div className="max-h-80 overflow-y-auto pr-1 -mr-1">
            <BookingsByPass
              bookings={pastBookings}
              passes={user?.passes || []}
              translatePassName={translatePassName}
              emptyMessage={ProfileTrans[language].noPastBookings}
              otherLabel={ProfileTrans[language].otherBookings}
              classesCountLabel={ProfileTrans[language].classesCount}
              renderRow={(b) => {
                const d = new Date(b.classId?.start ?? 0);
                const weekday = d.toLocaleDateString("en-GB", { timeZone: "Asia/Jerusalem", weekday: "short" });
                const day = d.toLocaleDateString("en-GB", { timeZone: "Asia/Jerusalem", day: "2-digit" });
                const month = d.toLocaleDateString("en-GB", { timeZone: "Asia/Jerusalem", month: "short" });
                const time = d.toLocaleTimeString("en-GB", { timeZone: "Asia/Jerusalem", hour: "2-digit", minute: "2-digit" });
                const dateStr = `${daysTrans[weekday.toLowerCase()] || weekday}, ${day} ${translateMonth(month)} ${d.getFullYear()} · ${time}`;
                return (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-2.5 px-3 hover:bg-amber-50/50 transition">
                    <span className="font-medium text-gray-800">
                      {b.classId?.description || b.classId?.title || "Class"}
                    </span>
                    <span className="text-sm text-gray-500">{dateStr}</span>
                  </div>
                );
              }}
            />
          </div>
        </div>

        {/* Upcoming bookings */}
        <div className="mb-6 sm:mb-8 bg-white p-4 sm:p-6 rounded-2xl shadow-lg border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">📅 {ProfileTrans[language].upcomingBookings}</h3>
          <BookingsByPass
            bookings={upcomingBookings}
            passes={user?.passes || []}
            translatePassName={translatePassName}
            emptyMessage={ProfileTrans[language].noUpcomingBookings}
            otherLabel={ProfileTrans[language].otherBookings}
            classesCountLabel={ProfileTrans[language].classesCount}
            renderRow={(b) => {
              const isOnline = b.classId?.type === "online" || b.online === true;
              const bgClass = isOnline ? "bg-blue-100" : "bg-green-100";
              const d = new Date(b.classId?.start ?? 0);
              const weekday = d.toLocaleDateString("en-GB", { timeZone: "Asia/Jerusalem", weekday: "short" });
              const day = d.toLocaleDateString("en-GB", { timeZone: "Asia/Jerusalem", day: "2-digit" });
              const month = d.toLocaleDateString("en-GB", { timeZone: "Asia/Jerusalem", month: "short" });
              const time = d.toLocaleTimeString("en-GB", { timeZone: "Asia/Jerusalem", hour: "2-digit", minute: "2-digit" });
              return (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedClass(b.classId);
                    setShowClassModal(true);
                  }}
                  className={`w-full text-left p-3 ${bgClass} hover:opacity-90 transition-opacity`}
                >
                  <span className="text-brown font-medium block">
                    {b.classId?.description || b.classId?.title || "Class"}
                  </span>
                  <span className="text-gray-600 text-sm block">
                    {`${daysTrans[weekday.toLowerCase()] || weekday}, ${day} ${translateMonth(month)} ${time}`}
                  </span>
                </button>
              );
            }}
          />
          <Link
            to="/schedule"
            className="inline-block mt-4 px-5 py-2.5 rounded-xl bg-brown text-white hover:bg-amber-800 text-sm font-semibold shadow-md hover:shadow transition"
          >
            {ProfileTrans[language].viewSchedule}
          </Link>
        </div>

        {showClassModal && selectedClass && (
          <ClassModal
            showModal={showClassModal}
            selectedClass={selectedClass}
            user={user}
            token={token}
            language={language}
            details={scheduleDetails}
            isAdmin={user?.role === "admin"}
            getBookingForClass={getBookingForClass}
            isClassDateWithinAnyPass={() => true}
            getActivePassForUserAndLocation={() => null}
            isPassExhausted={() => false}
            translateDayName={(day) => daysTrans[day?.toLowerCase()] || day}
            translateMonth={translateMonth}
            translateLocationName={translateLocationName}
            handleConfirmBooking={() => {}}
            handleCancelBooking={handleCancelBooking}
            cancelingFromModal={cancelingFromModal}
            setCancelingFromModal={setCancelingFromModal}
            bookingStatus={null}
            setShowModal={setShowClassModal}
            setSelectedClass={setSelectedClass}
            setShowPurchaseModal={() => {}}
            getActivePassForUser={() => null}
            loginWithRedirect={() => {}}
          />
        )}

        {/* Saved practices */}
        <div className="mb-6 sm:mb-8 bg-white p-4 sm:p-6 rounded-2xl shadow-lg border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">{ProfileTrans[language].classes}</h3>
          <Link
            to="/saved"
            onClick={() => trackPixelEvent("NavigateToSavedPractices")}
            className="text-brown hover:underline font-medium"
          >
            {ProfileTrans[language].savedClasses} {user?.savedPractices?.length ?? 0}
          </Link>
        </div>

        {/* Refer a friend — not for online-club-only accounts */}
        {user?.role !== "onlineClub" && (
          <div className="mb-6 sm:mb-8 text-center bg-white p-4 sm:p-6 rounded-2xl shadow-lg border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">{ProfileTrans[language].referFriendTitle}</h3>
            <p className="text-sm text-gray-600 mb-3">{ProfileTrans[language].referFriendHint}</p>
            {referralShareUrl ? (
              <button
                type="button"
                aria-label={ProfileTrans[language].referFriendCopy}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(referralShareUrl);
                    setReferralCopied(true);
                    setTimeout(() => setReferralCopied(false), 2000);
                  } catch (_) {
                    /* ignore */
                  }
                }}
                className="px-4 py-2 rounded-xl bg-brown text-white text-sm font-semibold hover:bg-amber-800 transition w-fit"
              >
                {referralCopied ? ProfileTrans[language].referFriendCopied : ProfileTrans[language].referFriendCopy}
              </button>
            ) : null}
          </div>
        )}

        {/* Current plan + account details */}
        <div className="mb-6 sm:mb-8 bg-white p-4 sm:p-6 rounded-2xl shadow-lg border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">{ProfileTrans[language].currentPlan}</h3>
            <div className="flex flex-wrap gap-x-2">
              <dt className="text-gray-500 font-medium">{ProfileTrans[language].status}:</dt>
              <dd className="text-gray-700 capitalize">{user?.role ?? "—"}</dd>
            </div>
            {user?.email && (
              <div className="flex flex-wrap gap-x-2 min-w-0">
                <dt className="text-gray-500 font-medium flex-shrink-0">{ProfileTrans[language].email}:</dt>
                <dd className="text-gray-700 truncate" title={user.email}>{user.email}</dd>
              </div>
            )}
        </div>

        {/* Telegram & Support */}
        <div className="w-fit mx-auto mb-10 mt-10">
          <a
            href="mailto:anna.joy.inbox@gmail.com?subject=FromOUOAM&body=Hey%20there"
            className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:border-amber-200 hover:shadow-md transition text-brown font-medium"
          >
            {ProfileTrans[language].contactSupport} (Email)
          </a>
        </div>

        {(() => {
          const hasStudio = hasActiveStudioPass(user);
          const hasOnlineClub = hasActiveOnlineClubPass(user);
          const sideBySideClass =
            "flex-1 min-w-0 max-w-md mt-5 -mb-5 w-full flex justify-center";
          if (hasStudio && hasOnlineClub) {
            return (
              <div className="flex flex-col md:flex-row flex-wrap justify-center items-stretch gap-4 md:gap-6 w-11/12 max-w-4xl mx-auto mb-10">
                <WhatsAppSection wrapClassName={sideBySideClass} />
                <TelegramSection isOnlineClubContext wrapClassName={sideBySideClass} />
              </div>
            );
          }
          if (hasOnlineClub) return <TelegramSection />;
          if (hasStudio) return <WhatsAppSection />;
          return <TelegramSection />;
        })()}

        <PWAInstallBanner />
      </div>
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div ref={modalRef} className="bg-white p-4 rounded-lg shadow-lg max-w-sm text-center">
            <h2 className="text-base font-semibold mb-4 text-center">Вы уверены, что хотите отменить подписку?</h2>
            <span className="text-gray-600 text-sm">Если сомневаетесь — </span>
            <a href="https://wa.me/972559116975"
              target="_blank"
              rel="noopener noreferrer" className="text-gray-600 text-sm mb-4 text-pink-600 underline">
              напишите мне в WhatsApp ❤️ <br />
            </a>
            <div className="flex justify-around mt-5">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded bg-green-300 hover:text-white text-sm"
              >
                Оставить
              </button>
              <button
                onClick={handleCancelSubscription}
                className="px-4 py-2 rounded bg-red-400 hover:text-white text-sm"
              >
                Отменить
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

